import Vue from 'vue';
import Vuex from 'vuex';
import { researcherApiService } from '@/services/researcherApiService';
import { authTokenService } from '@/services/authTokenService';
import { authService } from '@/services/authService';
import { resolve } from 'core-js/fn/promise';
import { Roles } from '@/common/Roles';
import { utilityService } from '@/services/utilityService';

Vue.use(Vuex);

const research = {
	state: {
		studies: {},
		studiesSortProperty: 'Name',
		tokenClaims: null,
		selectedStudyUuid: null,
		userInfo: {
			userUuid: '',
			firstName: '',
			lastName: '',
			organization: '',
			phoneNumber: '',
			email: '',
			studyRoles: []
		},
		statusUpdate: 'default',
		statusMessage: 'error1',
		studyVersions: [],
		// currentStudyTab: { tabIndex: 0, subTabIndex: 0 },
		currentStudyTabIndex: null,
		currentStudySubTabIndex: null,
		impersonatedUserUuid: null,
		alertTimeout: -1,
		shouldChangePassword: null
	},
	mutations: {
		addStudy (state, { study }) {
			// Date values are unix epochs, store them as integers in milliseconds
			study.dateStart = utilityService.normalizeEpoch(study.dateStart);
			study.dateEnd = utilityService.normalizeEpoch(study.dateEnd);
			study.dateRecorded = utilityService.normalizeEpoch(study.dateRecorded);

			// Must use Vue.set to update the map. Changes cannot be observed on properties that don't exist at initialization
			Vue.set(state.studies, study.studyUuid, study);
			window.sessionStorage.setItem('studies', JSON.stringify(state.studies));
		},
		deleteStudy (state, { studyUuid }) {
			Vue.delete(state.studies, studyUuid);
			window.sessionStorage.setItem('studies', JSON.stringify(state.studies));
		},
		/**
		 * Clear the studies map. Used when entering or leaving impersonation.
		 * @param {*} state
		 */
		clearStudies (state) {
			state.studies = {};
			window.sessionStorage.setItem('studies', JSON.stringify(state.studies));
		},
		setSelectedStudyUuid (state, { id }) {
			state.selectedStudyUuid = id;
			window.sessionStorage.setItem('selectedStudyUuid', id);
		},
		deleteSelectedStudyUuid (state) {
			state.selectedStudyUuid = null;
			window.sessionStorage.removeItem('selectedStudyUuid');
		},
		setUserInfo (state, { userInfo }) {
			state.userInfo = userInfo;
			window.sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
		},
		setStudyVersions (state, { studyVersions }) {
			console.log('setStudyVersions', studyVersions);
			state.studyVersions = studyVersions;
		},
		setCurrentStudyTab (state, { tabIndex, subTabIndex } = {}) {
			state.currentStudyTabIndex = tabIndex;
			state.currentStudySubTabIndex = subTabIndex;
			window.sessionStorage.setItem('currentStudyTabIndex', tabIndex);
			window.sessionStorage.setItem('currentStudySubTabIndex', subTabIndex);
		},
		setShouldChangePassword (state, { shouldChangePassword }) {
			state.shouldChangePassword = shouldChangePassword;
			window.sessionStorage.setItem('shouldChangePassword', shouldChangePassword);
		},
		setImpersonatedUserUuid (state, { impersonatedUserUuid }) {
			state.impersonatedUserUuid = impersonatedUserUuid;
			window.sessionStorage.setItem('impersonatedUserUuid', impersonatedUserUuid);
		},
		async clearStore (state, fullClear = false) {
			state.impersonatedUserUuid = '';
			window.sessionStorage.clear();
			// window.sessionStorage.removeItem('impersonatedUserUuid');
			// window.sessionStorage.removeItem('selectedStudyUuid');
			state.studies = {};
			state.selectedStudyUuid = null;
			state.userInfo = {
				userUuid: '',
				firstName: '',
				lastName: '',
				organization: '',
				phoneNumber: '',
				email: '',
				studyRoles: []
			};
			state.shouldChangePassword = null;

			console.log('Clearing store');

			if (fullClear) {
				state.tokenClaims = null;
			}

			return resolve;
		},
		setStudiesSortProperty (state, { studiesSortProperty }) {
			state.studiesSortProperty = studiesSortProperty;
		}
	},
	getters: {
		/**
		 * Gets the mapping of studies. Key: studyUuid, Value: study
		 * @param {*} state The Store state
		 * @returns {Map<string, Study>}
		 */
		getStudies: (state) => {
			if (Object.keys(state.studies).length === 0) {
				// Check the session storage for studies
				const studies = window.sessionStorage.getItem('studies');
				if (studies != null && Object.keys(JSON.parse(studies)).length > 0) {
					state.studies = JSON.parse(studies);
				}
			}
			return state.studies;
		},
		/**
		 * Gets the studies as an array.
		 * @param {*} state The Store state
		 * @returns {Array<Study>}
		 */
		getStudiesArray: (state, getters, actions) => {
			// Leverage the getStudies getter which will reload data from session storage if necessary
			// if (Object.keys(state.studies).length === 0) {
			// 	// Check the session storage for studies
			// 	const studies = window.sessionStorage.getItem('studies');
			// 	if (studies != null && Object.keys(JSON.parse(studies)).length > 0) {
			// 		state.studies = JSON.parse(studies);
			// 	}
			// }
			// const studiesArray = [];
			// for (var studyUuid in state.studies) {
			// 	studiesArray.push(state.studies[studyUuid]);
			// }
			const studies = getters.getStudies;
			const studiesArray = [];
			for (var studyUuid in studies) {
				studiesArray.push(studies[studyUuid]);
			}
			return studiesArray;
		},
		getStudyByUuid: (state, { studyUuid }) => {
			return state.studies[studyUuid];
		},
		getUser: (state) => {
			if (state.tokenClaims == null) {
				state.tokenClaims = authTokenService.getClaims();
			}
			return state.tokenClaims.user;
		},
		getSelectedStudy: (state, getters) => {
			// Note: Calling the getter for selectedStudyUuid does not work for some reason. Do it internally here
			// even though it should be its own getter to handle the load from session storage.
			if (state.selectedStudyUuid == null) {
				state.selectedStudyUuid = window.sessionStorage.getItem('selectedStudyUuid');
			}
			return getters.getStudies[state.selectedStudyUuid];
		},
		/*
		* This function only returns the studies that have been 'created', no 'draft'
		* studies are included.
		*/
		getStudyNames: (state) => {
			var studyNames = [];
			for (var id in state.studies) {
				if (state.studies[id].currentStudyStatus && state.studies[id].currentStudyStatus.toLowerCase() === 'created') {
					studyNames.push(state.studies[id].studyName);
				}
			};

			return studyNames;
		},
		getStudyObjects: (state) => {
			var studyNames = [];
			for (var id in state.studies) {
				if (state.studies[id].currentStudyStatus && state.studies[id].currentStudyStatus.toLowerCase() === 'created') {
					var myObject = {}
					myObject.studyNames = state.studies[id].studyName;
					myObject.studyUuids = state.studies[id].studyUuid;
					myObject.studyObjs = {
						name: state.studies[id].studyName,
						id: state.studies[id].studyUuid
					};
					studyNames.push(myObject);
				}
			};

			return studyNames;
		},
		getSelectedStudyUuid: (state) => {
			return state.selectedStudyUuid;
		},
		getUserId: async (state) => {
			if (state.tokenClaims == null) {
				state.tokenClaims = authTokenService.getClaims();
			}
			return Promise.resolve(state.tokenClaims.userId);
		},
		getRoles: (state) => {
			if (state.tokenClaims == null) {
				state.tokenClaims = authTokenService.getClaims();
			}
			return JSON.parse(state.tokenClaims.roles);
		},
		getStudyAccess: (state) => {
			if (state.tokenClaims == null) {
				state.tokenClaims = authTokenService.getClaims();
			}
			return JSON.parse(state.tokenClaims.StudyAccess);
		},
		getUserInfo: (state) => {
			if (state.userInfo.userUuid === '' && window.sessionStorage.getItem('userInfo') != null) {
				state.userInfo = JSON.parse(window.sessionStorage.getItem('userInfo'));
			}
			return state.userInfo;
		},
		getStatusUpdate: (state) => {
			return state.statusUpdate;
		},
		getStatusMessage: (state) => {
			return state.statusMessage
		},
		getActiveStudyVersions: (state) => {
			return state.studyVersions
				.filter(version => version.active === true)
				.sort((a, b) => b.versionId - a.versionId);
		},
		getAllStudyVersions: (state) => {
			return state.studyVersions;
		},
		getStudyVersion: (state) => (versionId) => {
			return state.studyVersions.find(version => version.versionId === versionId);
		},
		getStudyVersionRecipeModule: (state) => (versionId) => {
			if (state.studyVersions.some(version => version.versionId === versionId)) {
				return state.studyVersions.find(version => version.versionId === versionId).recipeModule;
			}
			return false;
		},
		getCurrentStudyTabIndex: (state) => {
			if (state.currentStudyTabIndex == null) {
				state.currentStudyTabIndex = window.sessionStorage.getItem('currentStudyTabIndex');
			}
			// if (Object.keys(state.currentStudyTab).length === 0) {
			// 	// Check the session storage for currentStudyTab object
			// 	const currentStudyTab = window.sessionStorage.getItem('currentStudyTab');
			// 	if (currentStudyTab != null && Object.keys(JSON.parse(currentStudyTab)).length > 0) {
			// 		state.currentStudyTab = JSON.parse(currentStudyTab);
			// 	}
			// }
			return state.currentStudyTabIndex;
		},
		getCurrentStudySubTabIndex: (state) => {
			if (state.currentStudySubTabIndex == null && window.sessionStorage.getItem('currentStudySubTabIndex') != null) {
				state.currentStudySubTabIndex = window.sessionStorage.getItem('currentStudySubTabIndex');
			}
			return state.currentStudySubTabIndex;
		},
		// TODO Why is this an async function
		getImpersonatedUserUuid: (state) => {
			let impersonatedUserUuid = state.impersonatedUserUuid;
			// Try loading from session storage
			if (impersonatedUserUuid === null) {
				const uuid = window.sessionStorage.getItem('impersonatedUserUuid');
				if (uuid !== null && uuid !== 'null') {
					impersonatedUserUuid = uuid;
					state.impersonatedUserUuid = uuid;
				}
			}

			return impersonatedUserUuid || null;
		},
		getAlertTimeout: async (state) => {
			return Promise.resolve(state.alertTimeout);
		},
		getTokenClaims: (state) => {
			if (state.tokenClaims == null) {
				state.tokenClaims = authTokenService.getClaims();
			}

			return state.tokenClaims;
		},
		getShouldChangePassword: (state) => {
			if (state.shouldChangePassword === null) {
				state.shouldChangePassword = authTokenService.getShouldChangePassword();
			}
			return state.shouldChangePassword;
		},
		isImpersonating: (state, getters) => {
			const impersonatedUserUuid = getters.getImpersonatedUserUuid;
			return impersonatedUserUuid !== null && impersonatedUserUuid !== '' && impersonatedUserUuid !== 'null';
		},
		isAdmin: (state, getters) => {
			const tokenClaims = getters.getTokenClaims;
			return tokenClaims && tokenClaims.roles && tokenClaims.roles.find(role => role.toLowerCase() === Roles.Admin.toLowerCase());
		},
		isLoggedIn: (state, getters) => {
			const tokenClaims = getters.getTokenClaims;
			return tokenClaims && tokenClaims.userId && tokenClaims.userId !== '';
		},
		getStudiesSortProperty: (state) => {
			return state.studiesSortProperty;
		}
	},
	actions: {
		async loadStudies ({ commit, state }) {
			var studyRoles = [];
			studyRoles = state.userInfo.studyRoles;

			// studyRoles can be null
			if (!studyRoles) {
				state.userInfo.studyRoles = [];
				return resolve;
			}

			// Empty out the study map before reloading
			commit('clearStudies');

			console.log('Loading studies...');

			for (var i = 0; i < studyRoles.length; i++) {
				await researcherApiService.loadStudy(studyRoles[i].studyUuid)
					.then(async function (response) {
						if (response && response.data !== '{}' && response.data) {
							commit('addStudy', { study: response.data });
						}
						return resolve;
					})
					.catch(async function (e) {
						console.log(`There was an error loading study ${studyRoles[i].studyUuid}`);
						// TODO Should have a message indicating studies could not be loaded?
					});
			}
			return resolve;
		},
		async refreshOneStudy ({ commit, state }) {
			console.log('Refreshing studies...');
			if (state.selectedStudyUuid) {
				var studyUuid = state.selectedStudyUuid
				commit('deleteStudy', { studyUuid });
				await researcherApiService.loadStudy(studyUuid)
					.then(async function (response) {
						if (response && response.data !== '{}' && response.data) {
							commit('addStudy', { study: response.data });
						}
						return resolve;
					})
					.catch(async function (e) {
						console.log(e);
					});
				return resolve;
			}
		},
		async loadUserInfo ({ commit, getters, dispatch }, userId) {
			// This is used to impersonate a user
			const impersonatedUserUuid = getters.getImpersonatedUserUuid;
			if (impersonatedUserUuid !== null) {
				console.log('switched userUuid with impersonatedUserUuid');
				userId = impersonatedUserUuid;
			}

			await researcherApiService.loadUserInfo(userId)
				.then(async function (response) {
					if (response && response.data) {
						console.log('Successfully loaded user info');
						// Save the user info in the store
						commit('setUserInfo', { userInfo: response.data });
						// Load all the user studies now that we have the study uuid list
						dispatch('loadStudies');
						return resolve;
					}
				})
				.catch(async function (e) {
					console.log(e);
				});
			return resolve;
		},
		saveUserInfo ({ commit }, user) {
			var userList = [];
			userList.push(user);
			researcherApiService.saveUserInfo(userList)
				.then(function (response) {
					if (response) {
						commit('setUserInfo', { userInfo: user });
					}
				})
				.catch(function (e) {
					console.log(e);
				});
		},
		async createStudy ({ commit }, study) {
			var studies = [];
			studies.push(study);
			var createStudyResponse = {};
			await researcherApiService.saveStudies(studies)
				.then(async function (response) {
					console.log('createStudy', response);
					if (response && response.data) {
						commit('addStudy', { study: response.data[0] });
						createStudyResponse.isSuccess = true;
						createStudyResponse.study = response.data;
						return resolve;
					} else {
						createStudyResponse.isResponse = false;
					}
				})
				.catch(async function (e) {
					createStudyResponse.isResponse = false;
					console.log('createStudy catch', e);
				})
			return Promise.resolve(createStudyResponse);
		},
		saveStudyUserInfo ({ dispatch, state }, studyRole) {
			var found = false;

			// studyRoles can be null
			if (!state.userInfo.studyRoles) {
				state.userInfo.studyRoles = [];
			}

			for (var i = 0; i < state.userInfo.studyRoles.length && !found; i++) {
				if (state.userInfo.studyRoles[i].studyId === studyRole.studyId) {
					found = true;
				}
			}

			// If it already exists don't do anything
			if (!found) {
				state.userInfo.studyRoles.push(studyRole);
				dispatch('saveUserInfo', state.userInfo);
			}
		},
		async deleteStudy ({ commit, dispatch, state }, studyUuid) {
			var deleteSuccess = false;
			await researcherApiService.deleteStudy(studyUuid)
				.then(async function (response) {
					if (response) {
						deleteSuccess = true;
						return Promise.resolve(deleteSuccess);
					}
				})
				.catch(async function (e) {
					console.log(e);
					return Promise.resolve(deleteSuccess);
				});

			if (deleteSuccess) {
				commit('deleteStudy', { studyUuid });
				var found = false;
				for (var i = 0; i < state.userInfo.studyRoles.length && !found; i++) {
					if (state.userInfo.studyRoles[i].studyUuid === studyUuid) {
						found = true;
						state.userInfo.studyRoles.splice(i, 1);
						dispatch('saveUserInfo', state.userInfo);
					}
				}
			}

			return Promise.resolve(deleteSuccess);
		},
		async updateStudy ({ state, commit }, study) {
			var studies = [];
			studies.push(study);
			var updated = false;
			await researcherApiService.saveStudies(studies)
				.then(async function (response) {
					if (response && response.data) {
						commit('addStudy', { study: response.data[0] });
						updated = true
						return resolve;
					}
				})
				.catch(async function (e) {
					console.log(e);
				})
			return Promise.resolve(updated);
		},
		loadTokenClaims (context) {
			context.state.tokenClaims = authTokenService.getClaims();
		},
		changeUpdateStatus ({ state }, { status, alertTimeout = 5000 }) {
			state.alertTimeout = alertTimeout;
			state.statusUpdate = null;
			state.statusUpdate = status;
		},
		changeUpdateStatusWithMessage ({ state }, { status, message, alertTimeout = 5000 }) {
			state.alertTimeout = alertTimeout;
			state.statusUpdate = null;
			state.statusMessage = null;
			state.statusMessage = message
			state.statusUpdate = status;
		},
		loadStudyVersions ({ commit }) {
			researcherApiService.getStudyVersions()
				.then(function (response) {
					if (response && response.data) {
						commit('setStudyVersions', { studyVersions: response.data });
					}
				})
				.catch(function (e) {
					console.log(`Error loading active versions: ${e}`);
				});
		},
		async impersonateUser ({ state, commit, dispatch }, impersonatedUserUuid) {
			commit('deleteSelectedStudyUuid');
			console.log(`Impersonating user ${impersonatedUserUuid}`);
			// await commit('clearStore');
			commit('setImpersonatedUserUuid', { impersonatedUserUuid: impersonatedUserUuid });
			await dispatch('loadUserInfo');
			return resolve;
		},
		async endImpersonation ({ state, commit, dispatch }) {
			// Clean up the impersonated user value and the selected study value (this may be tied to an impersonated user value)
			commit('setImpersonatedUserUuid', { impersonatedUserUuid: null });
			commit('setSelectedStudyUuid', { selectedStudyUuid: null });

			// Reload the user info and studies
			await dispatch('loadUserInfo', state.tokenClaims.userId);

			return resolve;
		},
		/**
		 * Handles the logout process. Clears the store values, the browser storage, and the redirect to the login screen.
		 * @param {*} param0.commit - VueJs commit function
		 * @param {*} param0.state - VueJs state
		 * @param {*} param1.fullClear - If true, clear the entire store
		 */
		logout ({ commit, dispatch }, { fullClear = false }) {
			// Clear the data out of the store
			commit('clearStore', { fullClear: fullClear });
			// Clear the data out of the browser storage and handle the redirect
			authService.logout();
		},
		async loginStartup ({ commit, dispatch, state }) {
			// TODO Remove the loadTokenClaims action
			state.tokenClaims = await authTokenService.getClaims();
			// this.$store.dispatch('loadTokenClaims');
			await dispatch('loadUserInfo', state.tokenClaims.userId);
			return resolve;
		}
	}
}

export default new Vuex.Store({
	modules: {
		research: research
	}
});
